import React, {FC, useState} from "react";
import {Dialog, DialogActions, DialogContent, DialogTitle, Paper, PaperProps} from "@mui/material";
import Button from "@mui/material/Button";
import {PDFViewer} from "@react-pdf/renderer";
import {PictureAsPdf} from "@mui/icons-material";
import {Shipment} from "../../../types/models/Shipment.ts";
import CordwoodListPdf from "./CordwoodListPdf.tsx";
import {useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import {PrimaryCutting} from "../../../types/models/Cutting.ts";
import {PrimaryForestOwner} from "../../../types/models/ForestOwner.ts";
import {PrimaryAddress} from "../../../../address/types/models/Address.ts";
import {useGetBuyerQuery} from "../../../apis/Buyer.api.ts";
import {PrimaryBuyer} from "../../../types/models/Buyer.ts";
import {useGetLocationQuery} from "../../../../location/apis/Location.api.ts";
import {PrimaryLocation} from "../../../../location/types/models/Location.ts";
import {useGetCordwoodPresentationQuery} from "../../../apis/Cordwood.api.ts";
import {timberClassApi} from "../../../apis/TimberClass.api.ts";
import {PrimaryTimberClass} from "../../../types/models/TimberClass.ts";
import {timberTypeApi} from "../../../apis/TimberType.api.ts";
import {PrimaryTimberType} from "../../../types/models/TimberType.ts";
import {useDispatch} from "react-redux";
import {useGetRoadCarrierQuery} from "../../../apis/RoadCarrier.api.ts";
import {useGetContractorQuery} from "../../../apis/Contractor.api.ts";
import {useGetProviderQuery} from "../../../apis/Provider.api.ts";
import {PrimaryProvider} from "../../../types/models/Provider.ts";
import {useGetContractorUserQuery} from "../../../apis/ContractorUser.api.ts";
import {PrimaryContractorUser} from "../../../types/models/ContractorUser.ts";
import {useGetTimberStorageQuery} from "../../../apis/TimberStorage.api.ts";
import {PrimaryTimberStorage} from "../../../types/models/TimberStorage.ts";
import { Trans } from "@lingui/react/macro";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";

function PaperComponent(props: PaperProps) {
    return (
        <Paper {...props} />
    );
}

type ICordwoodListDialogProps = {
    shipment: Shipment;
    copies?: number;
};

const EMPTY_LIST = {
    items: [],
    totalItems: 0,
    page: 0,
    pageSize: 100000,
    numPages: 0
}

const CordwoodListPdfDialog: FC<ICordwoodListDialogProps> = ({shipment, copies = 1}) => {

    const [open, setOpen] = useState(false);
    const [data, setData] = useState({items: [], totalItems: 0, totalSum: 0, isLoading: true})

    const {
        data: list = EMPTY_LIST, isLoading: getCordwoodListLoading
    } = useGetCordwoodPresentationQuery({
            list: {
                page: EMPTY_LIST.page,
                pageSize: EMPTY_LIST.pageSize,
            },
            filter: {
                shipmentIdRefs: [shipment.id],
            }
        },
        {skip: !open})

    const dispatch = useDispatch()

    React.useEffect(() => {
        let isSubscribed = true;

        if (open && !getCordwoodListLoading) {
            const fetchData = async () => {
                const data = {
                    items: [],
                    totalItems: list.totalItems,
                    sumM3: 0,
                    sumPrm: 0,
                    sumNpm: 0,
                    totalSumM3: 0,
                }
                for (const row of list.items) {
                    const timberClass = await dispatch(timberClassApi.endpoints.getTimberClass.initiate(PrimaryTimberClass.fromId(row.classId)))
                    const timberType = await dispatch(timberTypeApi.endpoints.getTimberType.initiate(PrimaryTimberType.fromId(row.timberTypeId)))

                    const rowWithNames = {
                        timberType: timberType?.data,
                        timberClass: timberClass?.data,
                        ...row,
                    }

                    data.items.push(rowWithNames)

                    switch (row.measurementType) {
                        case 'm3': data.sumM3 += row.volume; break;
                        case 'prm': data.sumPrm += row.volume; break;
                        case 'npm': data.sumNpm += row.volume; break;
                    }

                    data.totalSumM3 = data.totalSumM3 + row.volumeM3
                }

                if (isSubscribed) {
                    data.isLoading = false
                    setData(data)
                }
            }

            if (list) {
                fetchData()
                    .catch(console.error)
            }
        }

        return () => isSubscribed = false;
    }, [dispatch, list, open, getCordwoodListLoading])

    const {data: cutting} = useGetCuttingQuery(PrimaryCutting.fromId(shipment.cuttingId), {skip: !open})
    const {data: storage} = useGetTimberStorageQuery(PrimaryTimberStorage.fromId(shipment.storageId), {skip: !open})
    const {data: storageLocation} = useGetLocationQuery(PrimaryLocation.fromId(storage?.locationId), {skip: !storage?.locationId || !open})
    const {data: contractor} = useGetContractorQuery(PrimaryForestOwner.fromId(cutting?.contractorId), {skip: !open})
    const {data: provider} = useGetProviderQuery(PrimaryProvider.fromId(cutting?.providerId), {skip: !open})
    const {data: buyer} = useGetBuyerQuery(PrimaryBuyer.fromId(shipment.buyerId), {skip: !shipment.buyerId || !open})
    const {data: roadCarrier} = useGetRoadCarrierQuery(PrimaryAddress.fromId(shipment?.roadCarrierId), {skip: !shipment?.roadCarrierId || !open})
    const {data: sourceLocation} = useGetLocationQuery(PrimaryLocation.fromId(shipment.sourceLocationId), {skip: !shipment.sourceLocationId || !open})
    const {data: contractorUser} = useGetContractorUserQuery(PrimaryContractorUser.fromId(shipment.shippingContractorUserId), {skip: !open || !shipment.shippingContractorUserId})

    const handleClickOpen = React.useCallback(() => {
        setOpen(true);
    }, [setOpen])
    const handleClose = React.useCallback(() => {
        setOpen(false);
    }, [setOpen])

    return (
        <>
            <Button onClick={handleClickOpen}><PictureAsPdf/></Button>
            <Dialog
                open={open && data}
                onClose={handleClose}
                PaperComponent={PaperComponent}
                PaperProps={{
                    style: {
                        width: '70%',     // Custom width
                        height: '850px'     // Custom height
                    },
                }}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle>{shipment.key}</DialogTitle>
                <DialogContent>
                    {getCordwoodListLoading || data.isLoading ?
                        <GradientCircularProgress/>
                        :
                        <div>
                            <PDFViewer showToolbar={true} width="100%" height="700px">
                                <CordwoodListPdf
                                    shipment={shipment}
                                    sourceLocation={sourceLocation || storageLocation}
                                    contractor={contractor}
                                    buyer={buyer}
                                    provider={provider}
                                    roadCarrier={roadCarrier}
                                    contractorUser={contractorUser}
                                    list={data}
                                    numberOfCopies={copies}
                                />
                            </PDFViewer>
                        </div>
                    }
                </DialogContent>
                <DialogActions>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'row' }}>
                        <Trans>Number of copies</Trans>: {copies}
                    </Paper>
                    <Button onClick={handleClose} color="primary">
                        <Trans>Close</Trans>
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default CordwoodListPdfDialog
